/**
 * Dev-sync J1 — #psy-floating-icon, the minimized state of the psychrometric
 * chart popup.
 *
 * Port of the tail of psychrometric_chart.tmpl (:66-69) + initFloatingDrag
 * (psychrometric_chart.js:421-487). Unlike the Ratings icon (`.hide` class),
 * this one is shown via `.active` (#psy-floating-icon.active — common.css
 * :27081) and lives INSIDE the popup view's own host element, as a sibling of
 * the .psychrometric-chart-popup overlay — reproduced by the popup component
 * rendering both under one .psychrometric-chart-host.
 *
 * ★ THE ZOOM COMPENSATION IS LOAD-BEARING (js:434-445): the AHU board
 * applies CSS `zoom` to <body> (useBodyZoom). `left`/`top` on a fixed
 * descendant are authored in PRE-zoom space but rendered at value×zoom,
 * while clientX/getBoundingClientRect are POST-zoom — mixing them overshoots
 * the drag by 1/zoom. All math in visual space; divide by zoom once, right
 * before writing CSS. (The Ratings icon precedent skips this — it predates
 * the AHU board; do not "simplify" to match it.)
 *
 * Click-vs-drag: a press that never moves past 4px is a click and restores
 * the dialog (js:459-486). Pointer capture replaces the legacy
 * document-level mousemove/mouseup pair.
 */

import { useCallback, useRef } from 'react';

interface PsychrometricFloatingIconProps {
  /** the legacy `.active` class — shown iff minimized. */
  active: boolean;
  /** a non-drag pointerup IS the click → restore() (js:480-486). */
  onRestore: () => void;
}

const DRAG_SLOP = 4; // js:463 — |dx| > 4 || |dy| > 4

const PsychrometricFloatingIcon = ({ active, onRestore }: PsychrometricFloatingIconProps) => {
  const elRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origVisualLeft: number;
    origVisualTop: number;
    moved: boolean;
  } | null>(null);

  /** js:442-445 — CSS zoom on <body>, 1 when unset/invalid. */
  const zoomFactor = () => {
    const z = parseFloat(
      (getComputedStyle(document.body) as CSSStyleDeclaration & { zoom?: string }).zoom ?? ''
    );
    return z && z > 0 ? z : 1;
  };

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = elRef.current;
    if (!el || e.button !== 0) return;
    const rect = el.getBoundingClientRect();
    drag.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origVisualLeft: rect.left,
      origVisualTop: rect.top,
      moved: false,
    };
    el.setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const el = elRef.current;
    if (!d || !el || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) > DRAG_SLOP || Math.abs(dy) > DRAG_SLOP) d.moved = true;
    if (!d.moved) return;
    const rect = el.getBoundingClientRect();
    const z = zoomFactor();
    // Clamp inside the viewport, in visual space (js:468-471).
    const maxVisualLeft = window.innerWidth - rect.width;
    const maxVisualTop = window.innerHeight - rect.height;
    const newVisualLeft = Math.min(Math.max(0, d.origVisualLeft + dx), maxVisualLeft);
    const newVisualTop = Math.min(Math.max(0, d.origVisualTop + dy), maxVisualTop);
    el.style.left = `${newVisualLeft / z}px`;
    el.style.top = `${newVisualTop / z}px`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
  }, []);

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = drag.current;
      const el = elRef.current;
      drag.current = null;
      if (!d || !el) return;
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      if (!d.moved) onRestore();
    },
    [onRestore]
  );

  return (
    <div
      ref={elRef}
      id="psy-floating-icon"
      className={`floating-icon psy-floating-icon${active ? ' active' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <i className="icon-psychrometric-chart" />
      <span className="icon-title">Psychrometrics</span>
    </div>
  );
};

export default PsychrometricFloatingIcon;
